/**
 * Les fuseaux qu'on propose — PUR. La version librairie de la liste du cockpit
 * (`apps/cockpit/src/shared/timezone.ts`, `COMMON_TIMEZONES`) : on ne liste pas les 400 zones
 * d'`Intl` (un choix illisible), on liste les marchés servis, groupés par région, et on ajoute
 * celle de l'appareil en tête et la valeur courante si elle manque.
 *
 * Les noms de villes sont en français par défaut ; une app d'une autre langue passe sa liste.
 */
import { fuseauDeLAppareil } from "./dates";

export interface OptionFuseau {
  /** L'identifiant IANA (« Europe/Paris »). */
  id: string;
  /** Le nom qu'on écrit à côté d'une heure (« Paris »). */
  ville: string;
  /** Le groupe du sélecteur (« Europe »). */
  region: string;
}

export const FUSEAUX: readonly OptionFuseau[] = [
  { id: "Europe/Paris", ville: "Paris", region: "Europe" },
  { id: "Europe/London", ville: "Londres", region: "Europe" },
  { id: "Europe/Madrid", ville: "Madrid", region: "Europe" },
  { id: "Europe/Berlin", ville: "Berlin", region: "Europe" },
  { id: "Europe/Rome", ville: "Rome", region: "Europe" },
  { id: "Europe/Lisbon", ville: "Lisbonne", region: "Europe" },
  { id: "Europe/Bucharest", ville: "Bucarest", region: "Europe" },
  { id: "Europe/Athens", ville: "Athènes", region: "Europe" },
  { id: "Europe/Moscow", ville: "Moscou", region: "Europe" },
  { id: "America/New_York", ville: "New York", region: "Amériques" },
  { id: "America/Chicago", ville: "Chicago", region: "Amériques" },
  { id: "America/Denver", ville: "Denver", region: "Amériques" },
  { id: "America/Los_Angeles", ville: "Los Angeles", region: "Amériques" },
  { id: "America/Toronto", ville: "Toronto", region: "Amériques" },
  { id: "America/Mexico_City", ville: "Mexico", region: "Amériques" },
  { id: "America/Bogota", ville: "Bogotá", region: "Amériques" },
  { id: "America/Asuncion", ville: "Asunción", region: "Amériques" },
  { id: "America/Sao_Paulo", ville: "São Paulo", region: "Amériques" },
  { id: "America/Argentina/Buenos_Aires", ville: "Buenos Aires", region: "Amériques" },
  { id: "Africa/Casablanca", ville: "Casablanca", region: "Afrique" },
  { id: "Africa/Lagos", ville: "Lagos", region: "Afrique" },
  { id: "Africa/Johannesburg", ville: "Johannesburg", region: "Afrique" },
  { id: "Asia/Dubai", ville: "Dubaï", region: "Asie" },
  { id: "Asia/Istanbul", ville: "Istanbul", region: "Asie" },
  { id: "Asia/Kolkata", ville: "Calcutta", region: "Asie" },
  { id: "Asia/Bangkok", ville: "Bangkok", region: "Asie" },
  { id: "Asia/Singapore", ville: "Singapour", region: "Asie" },
  { id: "Asia/Manila", ville: "Manille", region: "Asie" },
  { id: "Asia/Shanghai", ville: "Shanghai", region: "Asie" },
  { id: "Asia/Tokyo", ville: "Tokyo", region: "Asie" },
  { id: "Australia/Sydney", ville: "Sydney", region: "Océanie" },
  { id: "Pacific/Auckland", ville: "Auckland", region: "Océanie" },
  { id: "UTC", ville: "UTC", region: "Autre" },
];

/**
 * Le nom court d'un fuseau, à écrire À CÔTÉ d'une heure : « Paris », « Asunción ». Hors liste, le
 * dernier segment IANA (« Indian/Antananarivo » → « Antananarivo »), jamais l'identifiant entier.
 */
export function libelleDuFuseau(fuseau: string, fuseaux: readonly OptionFuseau[] = FUSEAUX): string {
  const connu = fuseaux.find((z) => z.id === fuseau);
  if (connu) return connu.ville;
  return (fuseau.split("/").pop() ?? fuseau).replace(/_/g, " ");
}

export interface GroupeDeFuseaux {
  region: string;
  fuseaux: OptionFuseau[];
}

/**
 * Les groupes du sélecteur : l'appareil d'abord (« Détecté »), puis les régions de la liste dans
 * leur ordre, puis la valeur courante si la liste ne l'a pas (« Autre »). Chaque fuseau une fois.
 */
export function groupesDeFuseaux(
  valeur: string | null | undefined,
  options: { fuseaux?: readonly OptionFuseau[]; appareil?: string | null; detecte?: string; autre?: string } = {},
): GroupeDeFuseaux[] {
  const { fuseaux = FUSEAUX, appareil = fuseauDeLAppareil(), detecte = "Détecté", autre = "Autre" } = options;
  const groupes: GroupeDeFuseaux[] = [];
  const vus = new Set<string>();
  const poser = (region: string, f: OptionFuseau) => {
    if (vus.has(f.id)) return;
    vus.add(f.id);
    let g = groupes.find((x) => x.region === region);
    if (!g) {
      g = { region, fuseaux: [] };
      groupes.push(g);
    }
    g.fuseaux.push(f);
  };
  if (appareil) poser(detecte, { id: appareil, ville: libelleDuFuseau(appareil, fuseaux), region: detecte });
  for (const f of fuseaux) poser(f.region, f);
  if (valeur && !vus.has(valeur)) poser(autre, { id: valeur, ville: libelleDuFuseau(valeur, fuseaux), region: autre });
  return groupes;
}
